import React, { forwardRef, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CheckCircle,
  CheckOutlined,
  ChevronLeft,
  CreditCard,
  History,
  Warning
} from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  Snackbar,
  Typography,
  IconButton,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  Chip,
  Stack,
  Slide,
  Modal
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchCurrentUser,
  fetchUserBillingDetails
} from "../../features/auth/authSlice";
import {
  selectCurrentUser,
  selectCurrentUserBillingDetails
} from "../../features/auth/authSelectors";
import dayjs from "dayjs";
import CardLists from "./CardLists";
import useThemeColor from "../../utils/useTheme";
import {
  addSubscription,
  cancelSubscription,
  reactivateSubscription
} from "../../api/userApi";
import {
  allowedCountries,
  countryRates
} from "../../utils/countryRatesMapping";

const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const ConfirmModal = ({ open, handleClose, handleConfirm, styles }) => {
  return (
    <Modal open={open} onClose={handleClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          bgcolor: styles.lightGreyColor,
          boxShadow: 24,
          p: 2.2,
          borderRadius: 2,
          minWidth: 240,
          textAlign: "center"
        }}
      >
        <Typography variant="h6" sx={{ mb: 3, color: styles.color }}>
          Are you sure?
        </Typography>
        <Box sx={{ display: "flex", justifyContent: "center", gap: 2 }}>
          <Button
            onClick={handleConfirm}
            sx={{
              p: "8px 14px",
              background: "#368AD0",
              color: "#fcfcfc",
              textTransform: "none",
              fontWeight: "bold",
              "&:hover": {
                background: "#2D74B2"
              }
            }}
          >
            Yes
          </Button>
          <Button
            onClick={handleClose}
            sx={{
              p: "8px 14px",
              color: "#368AD0",
              textTransform: "none",
              fontWeight: "bold",
              backgroundColor: "#ffffff",
              "&:hover": {
                backgroundColor: "#f0f0f0"
              }
            }}
          >
            No
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};
const ManageBilling = ({ setOpenSubscriptionModal }) => {
  const navigate = useNavigate();
  const styles = useThemeColor();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [openReactivateModal, setOpenReactivateModal] = useState(null);
  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const currentSubscription = useSelector(selectCurrentUserBillingDetails);
  const currentUser = useSelector(selectCurrentUser);

  useEffect(() => {
    dispatch(fetchUserBillingDetails());
  }, [dispatch]);
  const currentCurrency = useMemo(
    () =>
      countryRates[
        allowedCountries?.includes(currentUser?.country) &&
        !currentUser?.bundleUserAmount
          ? currentUser?.country
          : "US"
      ],
    [currentUser]
  );
  const handleCancelSubscription = async () => {
    setLoading(true);
    await cancelSubscription(currentSubscription?.subscriptionId);
    setTimeout(async () => {
      setOpenModal(false);
      setLoading(false);
      setSnackbarMessage("Subscription cancelled successfully.");
      setOpenSnackbar(true);
      dispatch(fetchUserBillingDetails());
      dispatch(fetchCurrentUser());
    }, 1500);
  };
  const handleReactivateSubscription = async () => {
    setLoading(true);
    await reactivateSubscription(currentSubscription?.subscriptionId);
    setTimeout(async () => {
      setOpenConfirmModal(false);
      setLoading(false);
      setSnackbarMessage("Subscription cancellation revoked successfully.");
      setOpenSnackbar(true);
      dispatch(fetchUserBillingDetails());
      dispatch(fetchCurrentUser());
    }, 1500);
  };

  const handleRenewSubscription = async () => {
    try {
      const { session } = await addSubscription(
        openReactivateModal,
        currentUser?.email,
        false,
        true
      );
      if (session?.url) window.location.href = session?.url;
      else {
        setSnackbarMessage("Subscription Reactivated successfully.");
        setOpenSnackbar(true);
        setTimeout(() => {
          dispatch(fetchUserBillingDetails());
        }, 2000);
      }
    } catch (err) {
      console.log(err);
    } finally {
      setOpenReactivateModal(null);
    }
  };

  const renderBillingHistoryTable = () => {
    const history = currentSubscription?.billingHistory || [];
    return (
      <Card
        sx={{ boxShadow: "none", mt: 4, mx: 3, background: styles.background }}
        elevation={2}
      >
        <CardContent>
          <Box display="flex" alignItems="center" mb={3}>
            <History sx={{ mr: 1, color: "#1976d2" }} />
            <Typography variant="h6" fontWeight={500} color={styles.color}>
              Billing History
            </Typography>
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, color: styles.color }}>
                    Amount
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: styles.color }}>
                    Status
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: styles.color }}>
                    Date
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: styles.color }}>
                    Description
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {history.map((record, index) => (
                  <TableRow key={index} hover>
                    <TableCell sx={{ color: styles.color }}>
                      {currentCurrency?.symbol}
                      {record.amount}
                    </TableCell>
                    <TableCell sx={{ color: styles.color }}>
                      <Chip
                        label={record.status?.toUpperCase()}
                        color={record.status === "paid" ? "success" : "error"}
                        size="small"
                      />
                    </TableCell>
                    <TableCell sx={{ color: styles.color }}>
                      {dayjs(record.purchaseDate).format("D MMM YYYY")}
                    </TableCell>
                    <TableCell sx={{ color: styles.color }}>
                      <Stack spacing={1}>
                        {record.product.map((product, idx) => (
                          <Typography key={idx} variant="body2">
                            {product.description}
                          </Typography>
                        ))}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    );
  };

  return (
    <Box
      display={"flex"}
      width="100%"
      flexDirection="column"
      color={styles.color}
      backgroundColor={styles.background}
      overflow="auto"
    >
      {/* Header */}
      <Box display="flex" alignItems="center" gap={3} p="32px 20px 0">
        <IconButton onClick={() => navigate("/profile")}>
          <ChevronLeft sx={{ height: 35, width: 35, fill: styles.color }} />
        </IconButton>
        <Typography variant="h5" fontWeight="500">
          Manage Billing
        </Typography>
      </Box>

      {["Gym", "Affiliate", "Individual"]?.includes(currentUser?.userType) ? (
        <>
          {/* Subscription Info Card */}
          {["active", "canceled", "trialing", "past_due"].includes(
            currentSubscription?.subscriptionStatus
          ) ? (
            <Card
              sx={{
                mx: 3,
                mt: 4,
                background: styles.background,
                color: styles.color
              }}
              elevation={2}
            >
              <CardContent>
                <Box display="flex" alignItems="center" mb={3}>
                  <CreditCard sx={{ mr: 1, color: "#1976d2" }} />
                  <Typography variant="h6">Current Subscription</Typography>
                </Box>
                <Stack spacing={2}>
                  <Box
                    display="flex"
                    gap={{ xs: 0.5, sm: 10 }}
                    flexDirection={{ xs: "column", sm: "row" }}
                    justifyContent="space-between"
                    alignItems={{ xs: "flex-start", sm: "center" }}
                  >
                    <Typography variant="subtitle1" color={styles.color}>
                      Product
                    </Typography>
                    <Typography variant="body1" fontWeight="500">
                      {
                        currentSubscription?.currentSubscription?.[0]
                          ?.productName
                      }
                    </Typography>
                  </Box>
                  <Box
                    display="flex"
                    gap={{ xs: 0.5, sm: 10 }}
                    flexDirection={{ xs: "column", sm: "row" }}
                    justifyContent="space-between"
                    alignItems={{ xs: "flex-start", sm: "center" }}
                  >
                    <Typography variant="subtitle1" color={styles.color}>
                      Price
                    </Typography>
                    <Typography variant="body1" fontWeight="500">
                      {currentCurrency?.symbol}
                      {+currentSubscription?.currentSubscription?.[0]?.amount /
                        100}{" "}
                      /{" "}
                      {currentSubscription?.currentSubscription?.[0]?.interval}
                    </Typography>
                  </Box>
                  <Box
                    display="flex"
                    gap={{ xs: 0.5, sm: 10 }}
                    flexDirection={{ xs: "column", sm: "row" }}
                    justifyContent="space-between"
                    alignItems={{ xs: "flex-start", sm: "center" }}
                  >
                    <Typography variant="subtitle1" color={styles.color}>
                      Billing Cycle Start
                    </Typography>
                    <Typography variant="body1">
                      {currentSubscription?.billingCycleStartDate}
                    </Typography>
                  </Box>
                  <Box
                    display="flex"
                    gap={{ xs: 0.5, sm: 10 }}
                    flexDirection={{ xs: "column", sm: "row" }}
                    justifyContent="space-between"
                    alignItems={{ xs: "flex-start", sm: "center" }}
                  >
                    <Typography variant="subtitle1" color={styles.color}>
                      Next Billing Date
                    </Typography>
                    <Typography variant="body1">
                      {currentSubscription?.nextBillingDate}
                    </Typography>
                  </Box>
                  {currentUser?.isAdmin ||
                  currentSubscription?.subscriptionStatus === "past_due" ? (
                    <></>
                  ) : (
                    <Box display="flex" justifyContent="center">
                      <Button
                        variant="outlined"
                        onClick={() => {
                          if (
                            currentSubscription?.subscriptionStatus ===
                            "canceled"
                          ) {
                            return;
                          } else if (currentSubscription?.canReactivate) {
                            setOpenConfirmModal(true);
                          } else {
                            setOpenModal(true);
                          }
                        }}
                        disabled={
                          loading ||
                          currentSubscription?.subscriptionStatus === "canceled"
                        }
                        startIcon={
                          loading ? <CircularProgress size={20} /> : null
                        }
                        sx={{
                          color: currentSubscription?.canReactivate
                            ? "#03B55C"
                            : "#FF4000",
                          borderColor: currentSubscription?.canReactivate
                            ? "#03B55C"
                            : "#FF4000",
                          mt: 2,
                          width: { xs: "100%", sm: "35%" },
                          minWidth: { xs: "100%", sm: "250px" },
                          py: 1.5,
                          textTransform: "none",
                          "&:disabled": {
                            backgroundColor: "#A7C8E9",
                            color: "#64AEDD",
                            pointerEvents: "none"
                          }
                        }}
                      >
                        {currentSubscription?.subscriptionStatus === "canceled"
                          ? "Canceled"
                          : currentSubscription?.canReactivate
                          ? "Reactivate (Scheduled to Cancel)"
                          : "Cancel Subscription"}
                      </Button>
                    </Box>
                  )}
                  {currentSubscription?.subscriptionStatus === "canceled" && (
                    <div className="reactivate-options">
                      <Typography variant="h6">
                        Reactivate Subscription
                      </Typography>
                      <Box display={"flex"} gap={2} mt={2}>
                        <Button
                          sx={{ textTransform: "none", background: "#358ad0" }}
                          variant="contained"
                          onClick={() => setOpenReactivateModal("monthly")}
                          className="btn btn-primary me-2"
                        >
                          Reactivate Monthly
                        </Button>
                        <Button
                          sx={{ textTransform: "none", background: "#358ad0" }}
                          variant="contained"
                          onClick={() => setOpenReactivateModal("yearly")}
                          className="btn btn-success"
                        >
                          Reactivate Yearly
                        </Button>
                      </Box>
                    </div>
                  )}
                </Stack>
              </CardContent>
            </Card>
          ) : (
            <Alert severity="info" sx={{ mx: 4.5, mt: 4 }}>
              No active subscription found.
            </Alert>
          )}

          <CardLists setOpenSubscriptionModal={setOpenSubscriptionModal} />
        </>
      ) : (
        <></>
      )}
      {renderBillingHistoryTable()}

      {/* Cancel Subscription Dialog */}
      <Dialog
        TransitionComponent={Transition}
        TransitionProps={{
          timeout: {
            enter: 500,
            exit: 300
          }
        }}
        open={openModal}
        onClose={() => setOpenModal(false)}
        PaperProps={{
          sx: {
            borderRadius: 2,
            width: "400px",
            background: "#1c1c1c"
          }
        }}
      >
        <DialogTitle sx={{ textAlign: "center", pt: 4 }}>
          <Warning sx={{ fontSize: 48, color: "#FF4000", mb: 2 }} />
          <Typography variant="h6" fontWeight="600" color="#FCFCFC">
            Cancel Subscription?
          </Typography>
        </DialogTitle>
        <DialogContent>
          <DialogContentText textAlign="center" color="#FCFCFC">
            You will lose access to premium features at the end of your billing
            period.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 3, flexDirection: "column", gap: 1 }}>
          <Button
            variant="contained"
            sx={{
              background: "#FF4000"
            }}
            fullWidth
            onClick={handleCancelSubscription}
            disabled={loading}
          >
            {loading ? (
              <CircularProgress size={24} />
            ) : (
              "Yes, Cancel Subscription"
            )}
          </Button>
          <Button
            variant="outlined"
            fullWidth
            onClick={() => setOpenModal(false)}
          >
            No, Keep My Subscription
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        TransitionComponent={Transition}
        TransitionProps={{
          timeout: {
            enter: 500,
            exit: 300
          }
        }}
        open={!!openReactivateModal}
        onClose={() => setOpenReactivateModal(null)}
        PaperProps={{
          sx: {
            borderRadius: 2,
            width: "400px",
            background: "#1c1c1c"
          }
        }}
      >
        <DialogTitle sx={{ textAlign: "center", pt: 4 }}>
          <CheckCircle sx={{ fontSize: 48, color: "#358ad0", mb: 2 }} />
          <Typography variant="h6" fontWeight="600" color="#FCFCFC">
            Reactivate Subscription?
          </Typography>
        </DialogTitle>
        <DialogContent>
          <DialogContentText textAlign="center" fontSize={14} color="#FCFCFC">
            You will be charged immediately. <br />
            Your subscription cycle will be updated according to your selected
            plan moving forward.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 3, flexDirection: "column", gap: 1 }}>
          <Button
            variant="contained"
            sx={{
              background: "#358ad0"
            }}
            fullWidth
            onClick={handleRenewSubscription}
            disabled={loading}
          >
            {loading ? (
              <CircularProgress size={24} />
            ) : (
              "Yes, Reactivate Subscription"
            )}
          </Button>
          <Button
            variant="outlined"
            sx={{
              margin: "0 !important"
            }}
            fullWidth
            onClick={() => setOpenReactivateModal(null)}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={openSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        autoHideDuration={3000}
        onClose={() => setOpenSnackbar(false)}
      >
        <Alert
          onClose={() => setOpenSnackbar(false)}
          severity={snackbarMessage.includes("success") ? "success" : "error"}
          variant="filled"
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
      <ConfirmModal
        open={openConfirmModal}
        handleClose={() => setOpenConfirmModal(false)}
        handleConfirm={handleReactivateSubscription}
        styles={styles}
      />
    </Box>
  );
};

export default ManageBilling;
